<template>
  <!-- DM-11385: the sources an AI Overview inside the Knowledge Panel cites, in Google's order.
       Rendered by KPCard into the captured page (an iframe), right under the AI Overview text, so
       every style is inline: the app's stylesheets do not reach the iframe. Layout and colours
       follow the ticket's prototype. -->
  <div v-if="!sources.length" data-testid="kp-aio-none"
    style="border-top: 1px solid #ebebeb; padding-top: 8px; margin-top: 8px; color: #70757a; font-style: italic; font-size: 12px">
    No AI Overview in this panel
  </div>
  <div v-else data-testid="kp-aio-sources" :style="{ display: 'flex', flexDirection: 'column', gap: '4px', margin: '8px 0', fontFamily: 'inherit' }">
    <div :style="{ fontSize: compact ? '11px' : '12px', fontWeight: 600, color: '#202124', marginTop: '2px' }">
      Sources ({{ sources.length }})
    </div>
    <button v-for="(source, i) in sources" :key="i" type="button" data-testid="kp-aio-source-btn"
      :title="compact ? `${source.title || source.url} – ${display(source.url)}` : source.url"
      :style="rowStyle(source)" @click="openMarkup(source, $event)">
      <span style="flex: 0 0 auto; display: inline-flex"><favicon :url="source.url" :size="compact ? 14 : 18" /></span>
      <span v-if="compact" :style="ellipsis({ flex: '1 1 auto', minWidth: 0, color: '#003C8F', fontSize: '11px' })">
        {{ display(source.url) }}
      </span>
      <span v-else :style="{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }">
        <span :style="ellipsis({ color: '#003C8F', fontSize: '12px' })">{{ source.title || display(source.url) }}</span>
        <span :style="ellipsis({ fontSize: '12px', color: '#212529' })">{{ display(source.url) }}</span>
      </span>
      <span v-if="!compact" :style="{ flex: '0 0 auto', fontSize: '10px', color: '#40454C' }">{{ markupSummary(source) }}</span>
    </button>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Favicon from "@/components/Favicon.vue";
import {
  RATING_OPTIONS, OWNERSHIP_OPTIONS, AUTHORITY_OPTIONS, markupValues, markupFill, isUnmarkedValue,
} from "@/utils/markupOptions";

// markupFill gives the app's CSS variables (var(--rt-pos-bg)), which do not exist inside the
// captured page, so they are resolved to their colours in the app's document.
function resolveColor(value) {
  const name = /^var\((--[^)]+)\)$/.exec(value)?.[1];
  return name ? getComputedStyle(document.documentElement).getPropertyValue(name).trim() || "#fff" : value;
}

const optionName = (options, value) => options.find((o) => o.value === value)?.name;

export default {
  name: "KpAioSources",
  components: { Favicon },
  // only views that mount the Markup dialog (Timeline, the Knowledge Panel view) provide this
  inject: { markupEditable: { default: false } },
  props: {
    sources: { type: Array, required: true }, // [{ url, urlid, title, markup }]
    compact: { type: Boolean, default: false }, // the Knowledge Panel view's one-line rows
    ts: { type: Number, default: 0 }, // the card's date, as the Markup dialog uses it
    keyword: { type: String, default: "" },
  },
  computed: {
    ...mapGetters("markup", { markupLabels: "markupLabels" }),
    ...mapGetters("highlight", { highlightMode: "highlightMode" }),
    // the toolbar's markup mode ('' when markup is off)
    markupMode() {
      return this.markupLabels?.[0] || "";
    },
  },
  methods: {
    ...mapActions("markup", ["setMarkupResultData"]),
    display(url) {
      return (url || "").replace(/^https?:\/\//, "").replace(/^www\./, "");
    },
    ellipsis(style) {
      return { ...style, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left" };
    },
    rowStyle(source) {
      return {
        width: "100%", boxSizing: "border-box", display: "flex", alignItems: "center",
        gap: this.compact ? "6px" : "8px", minHeight: this.compact ? "26px" : "44px",
        padding: this.compact ? "0 6px" : "4px 8px", border: "1px solid #c5d6f3", borderRadius: "6px",
        background: this.markupMode ? resolveColor(markupFill(source.markup, this.markupMode)) : "#fff",
        font: "inherit", color: "inherit", textAlign: "left", cursor: this.markupEditable ? "pointer" : "default",
      };
    },
    // "Positive · Owned Domain · iAuthority": the rating, then ownership and authority when marked
    markupSummary(source) {
      const { rating, ownership, authority } = markupValues(source.markup);
      const parts = [optionName(RATING_OPTIONS, rating) || "Unmarked"];
      if (!isUnmarkedValue(ownership)) parts.push(optionName(OWNERSHIP_OPTIONS, ownership));
      if (!isUnmarkedValue(authority)) parts.push(optionName(AUTHORITY_OPTIONS, authority));
      return parts.filter(Boolean).join(" · ");
    },
    // The Markup dialog, as a click on a Timeline result opens it (MarkupLink.openDialog). The row
    // sits in the iframe, so its position is offset by the iframe's own.
    openMarkup(source, e) {
      // as MarkupLink.openDialog: no Markup dialog while highlighting or outlining (ctrl-click),
      // nor in a view without one
      if (!this.markupEditable || this.highlightMode || e.ctrlKey) {
        return;
      }
      const row = e.currentTarget.getBoundingClientRect();
      const frame = e.currentTarget.ownerDocument.defaultView?.frameElement?.getBoundingClientRect() || { top: 0, left: 0 };
      this.setMarkupResultData({
        // a copy: the store must not hold the view's own object, which the save's repaint then
        // updates (the Knowledge Panel view keeps its cards outside the store)
        resultData: JSON.parse(JSON.stringify(source)),
        ts: this.ts,
        resultType: "knowledgegraph",
        keyword: this.keyword,
        anchor: {
          top: row.top + frame.top, left: row.left + frame.left,
          right: row.right + frame.left, bottom: row.bottom + frame.top,
        },
      });
    },
  },
};
</script>
